import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Mail, Lock, ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useLogin } from '@/hooks/useLogin';
import { useNotifications } from '@/hooks/useNotifications';
import { useAppDispatch } from '@/hooks/redux';
import { loginSuccess, setUser } from '@/store/slices/authSlice';
import { parseJWT, cn } from '@/utils';
import { UserRole } from '@/types';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const { mutate: loginMutate, isPending } = useLogin();
  const { success: showSuccessToast, error: showErrorToast } = useNotifications();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = (data: LoginFormData) => {
    loginMutate(data, {
      onSuccess: (result) => {
        dispatch(loginSuccess({ accessToken: result.accessToken, expiresIn: result.expiresIn }));

        const payload = parseJWT(result.accessToken);
        dispatch(
          setUser({
            id: String(payload?.sub ?? ''),
            email: data.email,
            name: data.email,
            role: result.type === 'admin' ? UserRole.ADMIN : UserRole.USER,
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          })
        );

        showSuccessToast('Logged in successfully');
        navigate(from, { replace: true });
      },
      onError: (err) => {
        showErrorToast(err.response?.data?.message || 'Invalid email or password');
      },
    });
  };

  return (
    <div className="w-full space-y-6">
      {/* Brand Header */}
      <div className="flex flex-col items-center justify-center space-y-3">
        <div className="flex items-center space-x-2.5">
          {/* Logo SVG matching the branding */}
          <svg
            className="h-12 w-12 shrink-0 drop-shadow-[0_0_8px_rgba(243,34,125,0.45)] animate-pulse"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient id="mintGradLogoLogin" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F3227D"></stop>
                <stop offset="100%" stopColor="#FFB600"></stop>
              </linearGradient>
            </defs>
            <path
              d="M24 4C14 16 10 24 10 32C10 40 16 44 24 44C32 44 38 40 38 32C38 24 34 16 24 4Z"
              fill="url(#mintGradLogoLogin)"
              stroke="#000000"
              strokeWidth="3"
            ></path>
            <circle cx="24" cy="30" r="8" fill="#121212" stroke="#000000" strokeWidth="3"></circle>
            <circle cx="24" cy="30" r="4" fill="#FFB600"></circle>
          </svg>
          <div className="flex flex-col">
            <span className="text-2xl font-black tracking-wider text-white">MST-MINT</span>
            <div className="flex items-center space-x-1 mt-[-2px]">
              <div className="h-[1.5px] w-4 bg-[#FFB600]" />
              <span className="text-[9px] uppercase font-bold tracking-[0.25em] text-[#FFB600]">
                Admin Panel
              </span>
              <div className="h-[1.5px] w-4 bg-[#FFB600]" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Glassmorphic Login Card */}
      <div className="bg-[#121212]/75 backdrop-blur-lg border border-[#51107E]/40 rounded-2xl p-6 sm:p-8 shadow-[0_12px_40px_rgba(0,0,0,0.65)] hover:border-[#6F239B]/50 transition-all duration-300">
        <div className="space-y-1.5 text-center mb-6">
          <h2 className="text-xl font-bold tracking-tight text-white">Welcome Back!</h2>
          <p className="text-xs text-zinc-400">Sign in to your account to continue</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs font-semibold text-zinc-300">
              Email Address
            </Label>
            <div className="relative group">
              <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500 group-focus-within:text-[#FFB600] transition-colors duration-200" />
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                {...register('email')}
                className={cn(
                  'pl-10 h-11 bg-[#1A1A1A] border-[#2E2E2E] text-white placeholder-zinc-500 focus-visible:ring-1 focus-visible:ring-[#6F239B] focus-visible:border-[#6F239B] focus-visible:ring-offset-0',
                  errors.email ? 'border-destructive focus-visible:ring-destructive' : ''
                )}
              />
            </div>
            {errors.email && <p className="text-xs text-[#FE8D16] mt-1">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs font-semibold text-zinc-300">
              Password
            </Label>
            <div className="relative group">
              <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500 group-focus-within:text-[#FFB600] transition-colors duration-200" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                {...register('password')}
                className={cn(
                  'pl-10 pr-10 h-11 bg-[#1A1A1A] border-[#2E2E2E] text-white placeholder-zinc-500 focus-visible:ring-1 focus-visible:ring-[#6F239B] focus-visible:border-[#6F239B] focus-visible:ring-offset-0',
                  errors.password ? 'border-destructive focus-visible:ring-destructive' : ''
                )}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 px-2 hover:bg-transparent text-zinc-400 hover:text-white"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </Button>
            </div>
            {errors.password && (
              <p className="text-xs text-[#FE8D16] mt-1">{errors.password.message}</p>
            )}
          </div>

          {/* Remember me & Forgot Password */}
          <div className="flex items-center justify-between text-xs pt-1">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="remember"
                className="border-zinc-600 data-[state=checked]:bg-[#6F239B] data-[state=checked]:border-[#6F239B]"
              />
              <label
                htmlFor="remember"
                className="text-zinc-400 font-medium cursor-pointer select-none"
              >
                Remember me
              </label>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-11 bg-[#6F239B] hover:bg-[#51107E] text-white font-bold rounded-lg shadow-[0_0_15px_rgba(111,35,155,0.4)] hover:shadow-[0_0_25px_rgba(243,34,125,0.5)] transition-all duration-300 flex items-center justify-center gap-2 group cursor-pointer"
            disabled={isPending}
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <span>Sign In</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </>
            )}
          </Button>
        </form>
      </div>

      {/* Footer copyright */}
      <div className="text-center text-[10px] text-zinc-500 tracking-wider">
        <p>© 2025 MST-MINT. All rights reserved.</p>
      </div>
    </div>
  );
}
